import React, { useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import ArrowLeft from '@rover/icons/svg/arrow-left.svg';
import { Box, Button, Flex, Heading, Link, Text } from '@rover/kibble/core';
import roverTheme from '@rover/kibble/themes/roverTheme';
import Review from '@rover/react-lib/src/components/modals/Review';
import { useApiBaseUrlsIndexRetrieve } from '@rover/rsdk/src/apiClient/latest/api-base-urls-index-retrieve';
import { useApiReviewsAutomaticrecordresponseDetailCreate } from '@rover/rsdk/src/apiClient/latest/api-reviews-automaticrecordresponse-detail-create';
import { useApiReviewsRatingresponseDetailCreate } from '@rover/rsdk/src/apiClient/latest/api-reviews-ratingresponse-detail-create';
import type { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';

import { getOpkFromReviewUrl } from '../../utils/utils';

type ReplyProps = {
  review: ReviewDispatch;
  onBack: () => void;
  onClose: () => void;
  isAutomaticRecord?: boolean;
  sitterOpk: string;
};

const Tips = styled.ul`
  padding-left: 21px;
  margin-top: 0;
  margin-bottom: 32px;
  li {
    margin-bottom: 4px;
    line-height: 125%;
  }
`;

const StyledTextarea = styled.textarea<{ hasError: boolean; theme: typeof roverTheme }>`
  width: 100%;
  border: ${({ hasError, theme }) =>
    hasError ? `1px solid ${theme.colors.border.error}` : `none`};
  resize: vertical;
  padding: 12px;
  font-size: ${({ theme }) => theme.text[200].fontSize};
`;

const ReplyForm = ({
  review,
  onBack,
  onClose,
  isAutomaticRecord,
  sitterOpk,
}: ReplyProps): JSX.Element => {
  const [replyText, setReplyText] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { mutate: reviewRatingResponseMutate, isLoading: isRatingLoading } =
    useApiReviewsRatingresponseDetailCreate();
  const { mutate: automaticRecordResponseMutate, isLoading: isAutomaticLoading } =
    useApiReviewsAutomaticrecordresponseDetailCreate();
  const { data: webUrls } = useApiBaseUrlsIndexRetrieve();
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const isLoading = isRatingLoading || isAutomaticLoading;

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const handleReplyChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setError(false);
    setReplyText(event.target.value);
  };

  const sendReply = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (isLoading || replyText.length === 0) {
      if (replyText.length === 0) {
        setError(true);
      }
      return;
    }

    const mutate = isAutomaticRecord ? automaticRecordResponseMutate : reviewRatingResponseMutate;
    const reviewOpk = getOpkFromReviewUrl(review.url);

    mutate(
      {
        reviewOpk,
        // @ts-expect-error
        data: { response: replyText },
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const keyStr = JSON.stringify(query.queryKey);
              return keyStr.includes(`/api/v7/people/${sitterOpk}/reviews/`);
            },
            refetchType: 'all',
          });
          onBack();
        },
      }
    );
  };

  const ownerShortName = review && 'poster' in review ? review.poster.shortName : 'owner';

  return (
    <form onSubmit={sendReply}>
      <Box width="100%" pb="4x">
        <Flex mb="4x">
          <Box
            width="24px"
            height="24px"
            p="1x"
            onClick={onBack}
            data-testid="back-arrow"
            aria-label={t`Go back to reviews list`}
            role="button"
            sx={{ cursor: 'pointer' }}
          >
            <ArrowLeft width="100%" height="100%" aria-hidden="true" />
          </Box>
          <Heading size="300" mb="0x" ml="2x">
            {t`Reply to`}
          </Heading>
        </Flex>
        <Box sx={{ paddingBottom: '30px' }}>
          <Review review={review} full />
        </Box>
        <Box pb="4x">
          <StyledTextarea
            hasError={error}
            placeholder={t`Type your reply`}
            rows={4}
            onChange={handleReplyChange}
            ref={ref}
          />
          {error && (
            <Text size="100" textColor="error">
              {t`This field is required`}
            </Text>
          )}
        </Box>
        <Text fontWeight="semibold" pb="2x">
          {t`Please remember:`}
        </Text>
        <Tips>
          <li>{t`Other owners will see your response.`}</li>
          <li>{t`A brief, polite reply that sticks to facts is the most professional.`}</li>
          <li>
            {t`Replying to a review shows you're responsive, and you focus on being a caring sitter.`}
          </li>
          <li>
            <Trans>
              Not sure how to best reply? Call Rover Support. You can also read our{' '}
              <Link
                href={`${webUrls?.constants?.ROVER_REVIEW_POLICY_WEB_URL}`}
                target="_blank"
                rel="noreferrer"
              >
                Review Policy
              </Link>
            </Trans>
          </li>
        </Tips>
        <Box mx="1x">
          <Button variant="primary" fullWidth webProps={{ type: 'submit' }} mb="2x">
            {t`Reply to ${ownerShortName}`}
          </Button>
          <Button fullWidth onClick={onClose} mt="1x">
            {t`Close`}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default ReplyForm;
